import React from 'react';
import { motion } from 'framer-motion';

const widgetTypes = [
  {
    icon: '🔔',
    title: "Widget Alerte & Session (MonAlerte)",
    audience: "Sur votre écran d'accueil",
    description: "Suivez en un coup d'œil votre session en cours (alerte active, itinéraire suivi, partage de position...) et accédez en un appui à la navigation, au partage du lien de suivi ou à l'application.",
    points: [
      "Statut de session en temps réel",
      "Horloge, date et accès rapide à l'app",
      "Partage et copie du lien d'alerte",
      "Thème, couleurs et disposition personnalisables",
    ]
  },
  {
    icon: '🧒',
    title: "Widget Enfant (MonAlerte, côté parent)",
    audience: "Sur l'écran d'accueil du parent",
    description: "Gardez un œil sur chaque enfant sans ouvrir l'application : sa position, son activité et ses messages, directement posés sur votre écran.",
    points: [
      "Batterie, position, lieu (maison, école...) et heure de mise à jour",
      "Icône d'activité et nombre de messages non lus",
      "Alerte visuelle immédiate en cas d'urgence",
      "Actions rapides : ouvrir la fiche, discuter, rafraîchir",
    ]
  },
  {
    icon: '📲',
    title: "Widget MonAlerteEnfant (côté enfant)",
    audience: "Sur l'écran d'accueil de l'enfant",
    description: "Un accès direct et rassurant aux messages et au statut de protection, sans avoir à chercher l'application dans le téléphone.",
    points: [
      "Messages reçus des parents",
      "Statut de protection en cours",
      "Accès à l'agenda et aux évènements prévus",
      "Ouverture immédiate de l'application au moindre besoin",
    ]
  }
];

const containerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.12
    }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
};

const WidgetFirst = () => {
  return (
    <section
      id="widget-first"
      className="section-padding"
      style={{
        background: 'linear-gradient(180deg, #060d1a 0%, #050b14 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <div
        className="bg-glow-sphere"
        style={{
          width: '38vw',
          height: '38vw',
          background: 'rgba(56, 189, 248, 0.04)',
          top: '10%',
          left: '-12%'
        }}
      />

      <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>

        {/* Header */}
        <div className="text-center" style={{ marginBottom: '60px' }}>
          <span className="badge-gold">🧩 Pensé Widget d'abord</span>
          <h2 style={{
            fontSize: 'clamp(2rem, 4vw, 2.8rem)',
            color: 'var(--mga-white)',
            marginTop: '10px',
            marginBottom: '15px'
          }}>
            MonAlerte se vit <span style={{ color: 'var(--mga-gold)' }}>sans ouvrir l'application</span>.
          </h2>
          <p style={{
            maxWidth: '700px',
            margin: '0 auto',
            color: 'var(--mga-slate-300)',
            fontSize: '1.05rem'
          }}>
            MonAlerte et MonAlerteEnfant sont conçus autour de widgets entièrement configurables : couleurs, disposition, informations affichées. Trois widgets couvrent chaque besoin, posés directement sur l'écran d'accueil.
          </p>
        </div>

        {/* Grid of widget types */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-100px' }}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '24px',
            marginTop: '40px'
          }}
        >
          {widgetTypes.map((widget, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              className="glass-card"
              style={{
                padding: '30px 25px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                border: '1px solid rgba(255, 255, 255, 0.04)',
                background: 'rgba(11, 31, 58, 0.2)'
              }}
            >
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '16px',
                backgroundColor: 'rgba(56, 189, 248, 0.1)',
                border: '1px solid rgba(56, 189, 248, 0.25)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '2rem',
                marginBottom: '18px'
              }}>
                {widget.icon}
              </div>

              <span style={{
                fontSize: '0.72rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: 'var(--mga-gold)',
                marginBottom: '6px'
              }}>
                {widget.audience}
              </span>

              <h3 style={{
                color: 'var(--mga-white)',
                fontSize: '1.2rem',
                marginBottom: '10px',
                fontFamily: 'var(--font-display)'
              }}>
                {widget.title}
              </h3>

              <p style={{
                color: 'var(--mga-slate-300)',
                fontSize: '0.92rem',
                lineHeight: '1.6',
                margin: '0 0 16px 0'
              }}>
                {widget.description}
              </p>

              <ul style={{
                margin: 0,
                padding: 0,
                listStyle: 'none',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                width: '100%'
              }}>
                {widget.points.map((point, i) => (
                  <li key={i} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px',
                    fontSize: '0.85rem',
                    color: 'var(--mga-slate-300)',
                    lineHeight: '1.4'
                  }}>
                    <span style={{ color: '#38bdf8', flexShrink: 0 }}>✓</span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>

        <p style={{
          textAlign: 'center',
          maxWidth: '700px',
          margin: '40px auto 0',
          color: 'var(--mga-slate-400)',
          fontSize: '0.85rem',
          fontStyle: 'italic'
        }}>
          Chaque widget est personnalisable dans sa disposition, ses couleurs et les informations affichées, pour s'adapter exactement à ce que chacun veut voir en premier.
        </p>

      </div>
    </section>
  );
};

export default WidgetFirst;
